import { Dec, dec } from '../quantity/decimal.js';
import { getUnit } from '../units/catalog.js';
export const PRICE_STALE_DAYS_DEFAULT = 90;
export const PROMOTION_DEFAULT_DAYS = 14;
export const PRICE_CHANGE_MIN_PERCENT_DEFAULT = 3;
export const PRICE_CHANGE_MIN_ABSOLUTE = '0.05';
export const PRICE_INDEX_MIN_PRODUCTS_DEFAULT = 5;
export const PRICE_MAX = '100000';
const DAY_MS = 86_400_000;
/** Days since the epoch of a `YYYY-MM-DD` (or ISO) date; time zones play no role. */
export function dayNumber(date) {
    const [year, month, day] = date.slice(0, 10).split('-').map(Number);
    return Math.floor(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1) / DAY_MS);
}
export function dayString(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
export const money = (value) => value.toDecimalPlaces(2, Dec.ROUND_HALF_UP).toFixed(2);
/**
 * What a user types on the keypad: "1,19", "1.19", "1,5", "12", "1.234,50" → "1.19", "1.50", …
 * Null for anything that is not a plausible amount.
 */
export function parseMoneyInput(input) {
    let text = input.trim().replace(/[\s€$£]|CHF|EUR|USD|GBP/gi, '');
    // Grouped amounts ("1.234,50", "1,234.50") or a plain one with at most two decimals. "1,234"
    // alone is ambiguous and refused rather than guessed.
    if (/^\d{1,3}(\.\d{3})+,\d{1,2}$/.test(text))
        text = text.replace(/\./g, '').replace(',', '.');
    else if (/^\d{1,3}(,\d{3})+\.\d{1,2}$/.test(text))
        text = text.replace(/,/g, '');
    else if (/^\d*[.,]?\d{0,2}$/.test(text) && /\d/.test(text))
        text = text.replace(',', '.');
    else
        return null;
    if (text.startsWith('.'))
        text = `0${text}`;
    if (text.endsWith('.'))
        text = text.slice(0, -1);
    if (!/^\d+(\.\d{1,2})?$/.test(text))
        return null;
    const value = dec(text || '0');
    if (value.isNegative() || value.greaterThan(PRICE_MAX))
        return null;
    return money(value);
}
/** Price per kg, per litre or per piece – null when the size is unknown or not comparable. */
export function computeUnitPrice(input) {
    if (!input.quantity || !input.unitCode)
        return null;
    const unit = getUnit(input.unitCode);
    if (!unit)
        return null;
    const quantity = dec(input.quantity);
    if (!quantity.greaterThan(0))
        return null;
    const inBase = quantity.times(unit.baseFactor);
    let base;
    let divisor;
    if (unit.dimension === 'MASS') {
        base = 'KG';
        divisor = inBase.div(1000);
    }
    else if (unit.dimension === 'VOLUME') {
        base = 'L';
        divisor = inBase.div(1000);
    }
    else if (unit.code === 'piece') {
        base = 'PIECE';
        divisor = inBase;
    }
    else
        return null;
    return {
        unitPrice: dec(input.price).div(divisor).toDecimalPlaces(4, Dec.ROUND_HALF_UP).toString(),
        unitPriceBase: base,
    };
}
/** The unit price of an observation: loose goods by their reference quantity, else the package. */
export function unitPriceOf(observation) {
    if (observation.unitPrice && observation.unitPriceBase)
        return { unitPrice: observation.unitPrice, unitPriceBase: observation.unitPriceBase };
    if (observation.perQuantity && observation.perUnitCode)
        return computeUnitPrice({
            price: observation.price,
            quantity: observation.perQuantity,
            unitCode: observation.perUnitCode,
        });
    return computeUnitPrice({
        price: observation.price,
        quantity: observation.packageQuantity,
        unitCode: observation.packageUnitCode,
    });
}
/** More than 5× or less than ⅕ of the previous price: the classic missing comma. */
export function checkPricePlausibility(price, previous) {
    if (!previous)
        return 'OK';
    const before = dec(previous);
    if (!before.greaterThan(0))
        return 'OK';
    const now = dec(price);
    if (now.greaterThan(before.times(5)))
        return 'MUCH_HIGHER';
    if (now.lessThan(before.div(5)))
        return 'MUCH_LOWER';
    return 'OK';
}
/** A printed unit price that contradicts price ÷ size (more than 2 % apart) is flagged. */
export function unitPriceContradicts(printed, computed) {
    if (!computed)
        return false;
    const expected = dec(computed);
    if (!expected.greaterThan(0))
        return false;
    return dec(printed).minus(expected).abs().div(expected).greaterThan('0.02');
}
const newestFirst = (a, b) => dayNumber(b.observedAt) - dayNumber(a.observedAt) ||
    (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
/** A promotion counts until its end, or for 14 days when no end is known. */
export function promotionActive(observation, today) {
    if (!observation.isPromotion)
        return false;
    const end = observation.promotionEndsAt
        ? dayNumber(observation.promotionEndsAt)
        : dayNumber(observation.observedAt) + PROMOTION_DEFAULT_DAYS;
    return dayNumber(today) <= end;
}
function pickCurrent(candidates, today) {
    const sorted = candidates
        .filter((o) => dayNumber(o.observedAt) <= dayNumber(today))
        .sort(newestFirst);
    // The newest observation wins – unless it is a promotion that is over.
    return sorted.find((o) => !o.isPromotion || promotionActive(o, today)) ?? null;
}
/** "Price here" of one product or ingredient: branch → chain → none. */
export function currentPrice(observations, options) {
    const inCurrency = observations.filter((o) => o.currency === options.currency);
    const staleDays = options.staleDays ?? PRICE_STALE_DAYS_DEFAULT;
    const wrap = (observation, viaChain) => observation
        ? {
            observation,
            viaChain,
            stale: dayNumber(options.today) - dayNumber(observation.observedAt) > staleDays,
        }
        : null;
    const here = pickCurrent(inCurrency.filter((o) => o.storeId === options.store.id), options.today);
    if (here)
        return wrap(here, false);
    const chainId = options.store.chainId ?? options.store.id;
    return wrap(pickCurrent(inCurrency.filter((o) => o.chainId === chainId || o.storeId === chainId), options.today), true);
}
/** Latest price per store of one product, newest store first. */
export function currentPricesByStore(observations, options) {
    const byStore = new Map();
    for (const observation of observations) {
        const key = `${observation.storeId ?? observation.chainId ?? ''}|${observation.currency}`;
        byStore.set(key, [...(byStore.get(key) ?? []), observation]);
    }
    const staleDays = options.staleDays ?? PRICE_STALE_DAYS_DEFAULT;
    const out = [];
    for (const group of byStore.values()) {
        const observation = pickCurrent(group, options.today);
        if (observation)
            out.push({
                observation,
                viaChain: false,
                stale: dayNumber(options.today) - dayNumber(observation.observedAt) > staleDays,
            });
    }
    return out.sort((a, b) => newestFirst(a.observation, b.observation));
}
/** The cheapest current price with a store, in one currency; stale prices never win. */
export function cheapestPrice(observations, options) {
    const candidates = currentPricesByStore(observations.filter((o) => o.currency === options.currency && (o.storeId || o.chainId)), options).filter((c) => !c.stale);
    if (!candidates.length)
        return null;
    return candidates.reduce((best, next) => dec(next.observation.price).lessThan(best.observation.price) ? next : best);
}
export function roundPercent(percent) {
    const places = percent.abs().greaterThanOrEqualTo(10) ? 0 : 1;
    return percent.toDecimalPlaces(places, Dec.ROUND_HALF_UP).toString();
}
function packageInBase(observation) {
    if (!observation.packageQuantity || !observation.packageUnitCode)
        return null;
    const unit = getUnit(observation.packageUnitCode);
    if (!unit)
        return null;
    return dec(observation.packageQuantity).times(unit.baseFactor);
}
function packageDiffers(a, b) {
    const sizeA = packageInBase(a);
    const sizeB = packageInBase(b);
    if (!sizeA || !sizeB)
        return false;
    const unitA = getUnit(a.packageUnitCode);
    const unitB = getUnit(b.packageUnitCode);
    if (unitA?.dimension !== unitB?.dimension)
        return false;
    return !sizeA.equals(sizeB);
}
function compare(previous, current, minPercent) {
    let basis = 'PRICE';
    let before = dec(previous.price);
    let after = dec(current.price);
    let packageChange = null;
    if (packageDiffers(previous, current)) {
        const unitBefore = unitPriceOf(previous);
        const unitAfter = unitPriceOf(current);
        if (unitBefore && unitAfter && unitBefore.unitPriceBase === unitAfter.unitPriceBase) {
            basis = 'UNIT_PRICE';
            before = dec(unitBefore.unitPrice);
            after = dec(unitAfter.unitPrice);
            packageChange = {
                from: { quantity: previous.packageQuantity, unitCode: previous.packageUnitCode },
                to: { quantity: current.packageQuantity, unitCode: current.packageUnitCode },
            };
        }
    }
    if (!before.greaterThan(0) || after.equals(before))
        return null;
    const absolute = after.minus(before);
    const percent = absolute.div(before).times(100);
    return {
        current,
        previous,
        basis,
        absolute: money(absolute),
        percent: roundPercent(percent),
        direction: absolute.greaterThan(0) ? 'UP' : 'DOWN',
        noticeable: percent.abs().greaterThanOrEqualTo(minPercent) &&
            absolute.abs().greaterThanOrEqualTo(PRICE_CHANGE_MIN_ABSOLUTE),
        packageChange,
    };
}
const sameOffer = (a, b) => dec(a.price).equals(b.price) && !packageDiffers(a, b);
/**
 * The newest regular price against the regular price before it that differs – for one product
 * at one store or chain. Promotions are neither the old nor the new price, so the end of a
 * promotion is not an increase.
 */
export function priceChange(observations, options) {
    const regular = observations
        .filter((o) => !o.isPromotion && dayNumber(o.observedAt) <= dayNumber(options.at))
        .sort((a, b) => -newestFirst(a, b));
    const current = regular.at(-1);
    if (!current)
        return null;
    let since = current;
    let previous = null;
    for (let index = regular.length - 2; index >= 0; index -= 1) {
        const candidate = regular[index];
        if (candidate.currency !== current.currency)
            continue;
        if (sameOffer(candidate, current)) {
            since = candidate;
            continue;
        }
        previous = candidate;
        break;
    }
    if (!previous)
        return null;
    const change = compare(previous, current, options.minPercent ?? PRICE_CHANGE_MIN_PERCENT_DEFAULT);
    return change ? { ...change, since: since.observedAt } : null;
}
/** First and last regular price inside the window – the 12-month line and the chart. */
export function priceTrend(observations, windowDays, options) {
    const end = dayNumber(options.at);
    const inside = observations
        .filter((o) => dayNumber(o.observedAt) <= end && dayNumber(o.observedAt) >= end - windowDays)
        .sort((a, b) => -newestFirst(a, b));
    const regular = inside.filter((o) => !o.isPromotion);
    const first = regular[0];
    const last = regular.at(-1);
    if (!first || !last || first === last || first.currency !== last.currency)
        return null;
    const absolute = dec(last.price).minus(first.price);
    const base = dec(first.price);
    return {
        first: { price: first.price, observedAt: first.observedAt },
        last: { price: last.price, observedAt: last.observedAt },
        absolute: money(absolute),
        percent: base.greaterThan(0) ? roundPercent(absolute.div(base).times(100)) : '0',
        points: inside
            .filter((o) => o.currency === last.currency)
            .map((o) => ({ price: o.price, observedAt: o.observedAt, isPromotion: o.isPromotion })),
    };
}
export const PRICE_INDEX_TOLERANCE_DAYS = 30;
function nearest(candidates, day, from, to) {
    return (candidates
        .filter((o) => dayNumber(o.observedAt) >= from && dayNumber(o.observedAt) <= to)
        .sort((a, b) => Math.abs(dayNumber(a.observedAt) - day) - Math.abs(dayNumber(b.observedAt) - day) ||
        newestFirst(a, b))[0] ?? null);
}
/**
 * "Your prices here": a fixed basket of the products with a regular price at that chain both
 * near the start and near the end of the window, weighted by how often the household bought
 * them. Null below the minimum – a number from three products would be noise.
 */
export function storePriceIndex(observationsByProduct, purchases, windowDays, options) {
    const entries = observationsByProduct instanceof Map
        ? [...observationsByProduct.entries()]
        : Object.entries(observationsByProduct);
    const end = dayNumber(options.at);
    const start = end - windowDays;
    const minPercent = options.minPercent ?? PRICE_CHANGE_MIN_PERCENT_DEFAULT;
    const basket = [];
    let valueStart = dec(0);
    let valueEnd = dec(0);
    for (const [productId, observations] of entries) {
        const regular = observations.filter((o) => !o.isPromotion);
        const last = nearest(regular, end, end - PRICE_INDEX_TOLERANCE_DAYS, end);
        if (!last)
            continue;
        const first = nearest(regular.filter((o) => o !== last && o.currency === last.currency), start, start - PRICE_INDEX_TOLERANCE_DAYS, Math.min(start + PRICE_INDEX_TOLERANCE_DAYS, dayNumber(last.observedAt) - 1));
        if (!first)
            continue;
        const weight = Math.max(1, Math.floor(purchases[productId] ?? 1));
        // A smaller package at the same price is an increase: the old price is scaled to the new size.
        let startPrice = dec(first.price);
        if (packageDiffers(first, last)) {
            const from = packageInBase(first);
            const to = packageInBase(last);
            if (from && to && from.greaterThan(0))
                startPrice = startPrice.times(to).div(from);
        }
        valueStart = valueStart.plus(startPrice.times(weight));
        valueEnd = valueEnd.plus(dec(last.price).times(weight));
        basket.push({
            productId,
            start: first,
            end: last,
            weight,
            change: compare(first, last, minPercent),
        });
    }
    const minProducts = options.minProducts ?? PRICE_INDEX_MIN_PRODUCTS_DEFAULT;
    if (basket.length < minProducts || !valueStart.greaterThan(0))
        return null;
    const noticeable = basket.filter((entry) => entry.change?.noticeable);
    return {
        percent: roundPercent(valueEnd.minus(valueStart).div(valueStart).times(100)),
        products: basket.length,
        up: noticeable.filter((entry) => entry.change.direction === 'UP').length,
        down: noticeable.filter((entry) => entry.change.direction === 'DOWN').length,
        unchanged: basket.length - noticeable.length,
        top: noticeable.sort((a, b) => dec(b.change.percent).abs().comparedTo(dec(a.change.percent).abs())),
    };
}
//# sourceMappingURL=prices.js.map